<script >
import { ref } from '@vue/composition-api';
const __sfc_main = {};
__sfc_main.props = {
  selectedCompanyName: {
    type: String,
    default: ''
  },
  selectedTicker: {
    type: String,
    default: ''
  },
  selectedFolderNames: {
    type: Array,
    default: () => []
  },
  favoriteFolders: {
    type: Array,
    default: () => []
  },
  entryType: {
    type: String,
    default: 'finder'
  },
  entryValue: {
    type: String,
    default: ''
  },
  canUseFavorites: {
    type: Boolean,
    default: false
  },
  canManageFavoriteFolders: {
    type: Boolean,
    default: false
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const emit = __ctx.emit;
  const favoriteDraft = ref({
    name: '',
    color: '#0ea5e9'
  });
  const editingFavoriteName = ref('');
  const editingFavoriteDraft = ref({
    newName: '',
    newColor: '#0ea5e9'
  });
  const startFavoriteEdition = folder => {
    editingFavoriteName.value = folder.name;
    editingFavoriteDraft.value = {
      newName: folder.label,
      newColor: folder.color || '#0ea5e9'
    };
  };
  const resetFavoriteEdition = () => {
    editingFavoriteName.value = '';
    editingFavoriteDraft.value = {
      newName: '',
      newColor: '#0ea5e9'
    };
  };
  const submitFavoriteEdition = () => {
    if (!editingFavoriteName.value || !editingFavoriteDraft.value.newName.trim()) {
      return;
    }
    emit('rename-favorite-folder', {
      name: editingFavoriteName.value,
      newName: editingFavoriteDraft.value.newName.trim(),
      newColor: editingFavoriteDraft.value.newColor
    });
    resetFavoriteEdition();
  };
  const submitFavoriteCreate = () => {
    if (!favoriteDraft.value.name.trim()) {
      return;
    }
    emit('create-favorite-folder', {
      name: favoriteDraft.value.name.trim(),
      color: favoriteDraft.value.color
    });
    favoriteDraft.value = {
      name: '',
      color: '#0ea5e9'
    };
  };
  return {
    emit,
    favoriteDraft,
    editingFavoriteName,
    editingFavoriteDraft,
    startFavoriteEdition,
    resetFavoriteEdition,
    submitFavoriteEdition,
    submitFavoriteCreate
  };
};
export default __sfc_main;
</script>

<template>
  <div class="charting-workspace-fragment">
    <section class="charting-workspace-drawer-section">
        <div class="flex items-center justify-between gap-3">
            <div>
                <div class="text-sm font-semibold text-surface-900 dark:text-surface-0">Ticker activo</div>
                <div class="mt-1 text-sm text-muted-color">{{ selectedTicker || 'Sin ticker seleccionado' }}</div>
                <div v-if="selectedCompanyName" class="mt-1 text-sm text-muted-color">{{ selectedCompanyName }}</div>
            </div>
            <Tag v-if="!canUseFavorites" value="Restringido" severity="warn" />
            <Tag v-else-if="selectedFolderNames.length" :value="`${selectedFolderNames.length} ${selectedFolderNames.length === 1 ? 'carpeta' : 'carpetas'}`" severity="secondary" />
        </div>

        <div v-if="!canUseFavorites" class="brand-empty-state mt-4 rounded-xl border border-dashed p-4 text-sm">Tu plan actual no permite gestionar favoritos.</div>
        <div v-else-if="selectedTicker && favoriteFolders.length" class="mt-4 flex flex-wrap gap-2">
            <Button
                v-for="folder in favoriteFolders"
                :key="`toggle-${folder.id}`"
                :label="folder.label"
                size="small"
                :severity="selectedFolderNames.includes(folder.name) ? 'primary' : 'secondary'"
                :outlined="!selectedFolderNames.includes(folder.name)"
                @click="emit('toggle-favorite-folder', folder.name)"
            />
        </div>
        <div v-else class="brand-empty-state mt-4 rounded-xl border border-dashed p-4 text-sm">Selecciona un ticker y crea carpetas para gestionar favoritos.</div>
    </section>

    <section class="charting-workspace-drawer-section">
        <div class="flex items-center justify-between gap-3">
            <div class="text-xs font-semibold uppercase tracking-wide text-muted-color">Carpetas</div>
            <Tag v-if="!canUseFavorites" value="Restringido" severity="warn" />
            <Tag v-else :value="String(favoriteFolders.length)" severity="secondary" />
        </div>

        <div v-if="!canUseFavorites" class="brand-empty-state mt-3 rounded-xl border border-dashed p-4 text-sm">Tus favoritos no están disponibles con el acceso actual.</div>
        <div v-else-if="favoriteFolders.length" class="mt-3 flex flex-col gap-3">
            <div v-for="folder in favoriteFolders" :key="folder.id" class="charting-workspace-management-row" :class="{ 'is-editing': editingFavoriteName === folder.name }">
                <template v-if="editingFavoriteName === folder.name">
                    <div class="flex items-center gap-3">
                        <input v-model="editingFavoriteDraft.newColor" type="color" class="charting-workspace-color-input" />
                        <InputText v-model="editingFavoriteDraft.newName" class="w-full" />
                    </div>
                    <div class="mt-3 flex gap-2">
                        <Button label="Guardar" size="small" @click="submitFavoriteEdition" />
                        <Button label="Cancelar" size="small" severity="secondary" outlined @click="resetFavoriteEdition" />
                    </div>
                </template>
                <template v-else>
                    <button type="button" class="charting-workspace-drawer-row" :class="{ 'is-active': entryType === 'favorites' && entryValue === folder.name }" @click="emit('open-source', { type: 'favorites', value: folder.name })">
                        <span class="flex min-w-0 items-center gap-3">
                            <span class="charting-workspace-folder-dot" :style="{ backgroundColor: folder.color }"></span>
                            <span class="truncate font-medium">{{ folder.label }}</span>
                        </span>
                        <i class="pi pi-chevron-right text-xs text-muted-color"></i>
                    </button>
                    <div v-if="canManageFavoriteFolders" class="charting-folder-actions flex gap-2">
                        <Button label="Editar" size="small" severity="secondary" outlined @click="startFavoriteEdition(folder)" />
                        <Button label="Eliminar" size="small" severity="danger" text @click="emit('remove-favorite-folder', folder.name)" />
                    </div>
                </template>
            </div>
        </div>
        <div v-else class="brand-empty-state mt-3 rounded-xl border border-dashed p-4 text-sm">No hay carpetas de favoritos visibles.</div>

        <div v-if="canUseFavorites && canManageFavoriteFolders" class="mt-4 border-t border-surface-200 pt-4 dark:border-surface-800">
            <div class="text-xs font-semibold uppercase tracking-wide text-muted-color">Nueva carpeta</div>
            <div class="mt-3 flex items-center gap-3">
                <input v-model="favoriteDraft.color" type="color" class="charting-workspace-color-input" />
                <InputText v-model="favoriteDraft.name" class="w-full" placeholder="Nombre de la carpeta" />
            </div>
            <Button class="mt-3" label="Crear carpeta" icon="pi pi-plus" :disabled="!favoriteDraft.name.trim()" @click="submitFavoriteCreate" />
        </div>
    </section>
  </div>
</template>

<style scoped>
.charting-workspace-management-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.5rem 0;
    border: 0;
    border-bottom: 1px solid var(--charting-b-border-color);
    border-radius: 0;
    background: transparent;
}
.charting-workspace-management-row.is-editing { display: block; padding: 0.75rem 0; }
.charting-workspace-drawer-row { flex: 1 1 12rem; min-width: 0; border-color: transparent; border-radius: 0.65rem; }
.charting-folder-actions { margin-left: auto; }
</style>
